// CampaignForm.jsx

import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addCampaignAsync,
  updateCampaignAsync,
  fetchCampaigns,
  selectCampaigns,
  
} from "./campaignSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./CampaignForm.css";

function CampaignForm({ campaign, onClose, onHandleClose }) {
  const dispatch = useDispatch();
  const campaigns = useSelector(selectCampaigns);
  const [name, setName] = useState("");
  const [subDomain, setSubDomain] = useState("");
  const [showForm, setShowForm] = useState(true);
  const [leadQuestionNumber, setLeadQuestionNumber] = useState(0);
  const [leadFields, setLeadFields] = useState({
    fullName: false,
    email: false,
    mobile: false,
  }); 
  const [fallBackUrl, setFallBackUrl] = useState(
    campaign ? campaign.fallBackUrl ?? "" : ""
  );
  const [nameError, setNameError] = useState("");
  const [subDomainError, setSubDomainError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (campaign) {
      setName(campaign.name || "");
      setSubDomain(campaign.subDomain || "");
      setLeadQuestionNumber(campaign.body.leadQuestionNumber || 0); 
      setLeadFields(campaign.body.leadFields || { fullName: false, email: false, mobile: false }); 
      setFallBackUrl(campaign.fallBackUrl || "");
    }
  }, [campaign]);
  

  const validateName = (value) => {
    if (!value || value.length < 3 || value.length > 30) {
      setNameError("Name length must be between 3 and 30 characters.");
      return false;
    }
    setNameError("");
    return true;
  };

  const validateSubDomain = (value) => {
    const subDomainRegex = /^[a-z0-9]{3,20}$/;
    if (!value || value.length < 3 || value.length > 20) {
      setSubDomainError(
        "Subdomain length must be between 3 and 20 characters."
      );
      return false;
    } else if (!subDomainRegex.test(value)) {
      setSubDomainError(
        "Subdomain must contain only lowercase letters and digits."
      );
      return false;
    }
    setSubDomainError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateName(name) && validateSubDomain(subDomain)) {
      setLoading(true);

      const campaignData = {
        name,
        subDomain,
        fallBackUrl,
        body: {
          leadQuestionNumber,
          leadFields,
        },
      };
      
      console.log(JSON.stringify(campaignData, null, 2));
      try {
        if (campaign) {
          const existingCampaign = campaigns.find(
            (c) => c.subDomain === subDomain && c.id !== campaign.id
          );
          if (existingCampaign) {
            toast.error(
              "Subdomain already exists in another campaign. Please use a different subdomain."
            );
          } else {
            await dispatch(
              updateCampaignAsync({
                campaignId: campaign.id,
                updatedCampaignData: campaignData,
              })
            );
            setShowForm(false);
            dispatch(fetchCampaigns());
            onHandleClose();
            toast.success("Campaign updated successfully");
          }
        } else {
          const existingCampaign = campaigns.find(
            (campaign) => campaign.subDomain === subDomain
          );
          if (existingCampaign) {
            toast.error(
              "Subdomain already exists. Please use a different subdomain."
            );
          } else {
            await dispatch(addCampaignAsync(campaignData));
            dispatch(fetchCampaigns());
            setShowForm(false);
            onHandleClose();
            toast.success("Campaign added successfully");
          }
        }
      } catch (error) {
        console.error("Error:", error);
        toast.error("An error occurred");
      } finally {
        setTimeout(() => {
          setLoading(false);
        }, 2000);
      }
    }
  }      

  const handleLeadGenerationChange = (value) => {
    setLeadQuestionNumber(value === "yes" ? 1 : 0);
    if (value === "no") {
      setLeadFields({
        fullName: false,
        email: false,
        mobile: false,
      });
    }
  };

  const handleLeadFieldChange = (field) => {
    setLeadFields((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  return (
    <section className={`form ${showForm ? "show" : "hide"}`}>
      <div className="form-header">
        <h2>{campaign ? "Edit Campaign" : "Add New Campaign"}</h2>
        <div className="close-icon" onClick={onHandleClose}>
          <FontAwesomeIcon icon={faTimes} />
        </div>
      </div>
      <form onSubmit={handleSubmit}>
        <div className={`form-group ${nameError && "error"}`}>
          <label htmlFor="name">Name:</label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={(e) => validateName(e.target.value)}
          />
          {nameError && <div className="error">{nameError}</div>}
        </div>
        <div className={`form-group ${subDomainError && "error"}`}>
          <label htmlFor="subDomain">SubDomain:</label>
          <input
            type="text"
            id="subDomain"
            value={subDomain}
            onChange={(e) => setSubDomain(e.target.value)}
            onBlur={(e) => validateSubDomain(e.target.value)}
          />
          {subDomainError && <div className="error">{subDomainError}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="fallBackUrl">Fallback URL:</label>
          <input
            type="text"
            id="fallBackUrl"
            value={fallBackUrl}
            onChange={(e) => setFallBackUrl(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Do you want to generate leads?</label>
          <div className="lead-gen-options">
            <label>
              <input
                type="radio"
                name="leadGeneration"
                value="no"
                checked={leadQuestionNumber === 0}
                onChange={() => handleLeadGenerationChange("no")}
              />
              No
            </label>
            <label>
              <input
                type="radio"
                name="leadGeneration"
                value="yes"
                checked={leadQuestionNumber !== 0}
                onChange={() => handleLeadGenerationChange("yes")}
              />
              Yes
            </label>
          </div>
        </div>
        {leadQuestionNumber !== 0 && (
          <div className="lead-info">
            <p>Generate leads following which question number?</p>
            <input
              type="number"
              min="1"
              value={leadQuestionNumber}
              required
              onChange={(e) => setLeadQuestionNumber(parseInt(e.target.value))}
            />
          </div>
        )}
        {leadQuestionNumber !== 0 && (
          <div className="lead-info">
            <p>Select the information you want to collect:</p>
            <div className="lead-info-group">
              <label>
                <input
                  type="checkbox"
                  checked={leadFields.fullName}
                  onChange={() => handleLeadFieldChange("fullName")}
                />
                Full Name
              </label>
            </div>
            <div className="lead-info-group">
              <label>
                <input
                  type="checkbox"
                  checked={leadFields.email}
                  onChange={() => handleLeadFieldChange("email")}
                />
                Email
              </label>
            </div>
            <div className="lead-info-group">
              <label>
                <input
                  type="checkbox"
                  checked={leadFields.mobile}
                  onChange={() => handleLeadFieldChange("mobile")}
                />
                Mobile
              </label>
            </div>
          </div>
        )}
        {loading ? (
          <div className="spinner">
            <div className="bounce1"></div>
            <div className="bounce2"></div>
            <div className="bounce3"></div>
          </div>
        ) : (
          <button id="submitBtn" type="submit">
            Submit
          </button>
        )}
      </form>
    </section>
  );
}

export default CampaignForm;
